import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { BusinessOutlined, ComputerOutlined } from "@mui/icons-material";

/**
 * ModalitySelector - Componente para seleccionar modalidad de cursos (Presencial/Virtual)
 * methodology = null  => ninguno seleccionado (estado inicial)
 * methodology = false => Presencial seleccionado
 * methodology = true  => Virtual seleccionado
 */
const ModalitySelector = ({ methodology, onSelectMode, isVirtualDisabled }) => {

    const baseCard = {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: { xs: 165, sm: 205, md: 225 },
        height: { xs: 165, sm: 205, md: 225 },
        borderRadius: "50%",
        cursor: "pointer",
        transition: "all 0.3s ease-in-out",
        border: "4px solid",
        userSelect: "none",
        padding: 2,
        textAlign: "center",
    };

    // ── Estilos PRESENCIAL ──────────────────────────────────────────────────
    const presencialSelected = {
        background: "linear-gradient(145deg, #22577A 0%, #1a4560 100%)",
        borderColor: "#5a9ab5",
        boxShadow: "0 0 14px rgba(90, 154, 181, 0.25), 0 12px 32px rgba(34, 87, 122, 0.4)",
        animation: "pulseGlow 2.5s ease-in-out infinite",
        "@keyframes pulseGlow": {
            "0%": { boxShadow: "0 0 10px rgba(90, 154, 181, 0.2), 0 12px 32px rgba(34, 87, 122, 0.4)" },
            "50%": { boxShadow: "0 0 20px rgba(90, 154, 181, 0.3), 0 12px 32px rgba(34, 87, 122, 0.4)" },
            "100%": { boxShadow: "0 0 10px rgba(90, 154, 181, 0.2), 0 12px 32px rgba(34, 87, 122, 0.4)" },
        },
        "&:hover": { boxShadow: "0 0 22px rgba(90, 154, 181, 0.35), 0 16px 40px rgba(34, 87, 122, 0.5)" },
    };

    const presencialDefault = {
        background: "linear-gradient(145deg, #3d7ea6 0%, #2d6e96 100%)",
        borderColor: "#256080",
        boxShadow: "0 8px 20px rgba(34, 87, 122, 0.25)",
        "&:hover": { boxShadow: "0 10px 28px rgba(34, 87, 122, 0.4)" },
    };

    // ── Estilos VIRTUAL ─────────────────────────────────────────────────────
    const virtualSelected = {
        background: "linear-gradient(145deg, #5c35a0 0%, #3d1f7a 100%)",
        borderColor: "#9b7ed4",
        boxShadow: "0 0 14px rgba(156, 126, 212, 0.25), 0 12px 32px rgba(92, 53, 160, 0.4)",
        animation: "pulseGlowViolet 2.5s ease-in-out infinite",
        "@keyframes pulseGlowViolet": {
            "0%": { boxShadow: "0 0 10px rgba(156, 126, 212, 0.2), 0 12px 32px rgba(92, 53, 160, 0.4)" },
            "50%": { boxShadow: "0 0 20px rgba(156, 126, 212, 0.3), 0 12px 32px rgba(92, 53, 160, 0.4)" },
            "100%": { boxShadow: "0 0 10px rgba(156, 126, 212, 0.2), 0 12px 32px rgba(92, 53, 160, 0.4)" },
        },
        "&:hover": { boxShadow: "0 0 22px rgba(156, 126, 212, 0.35), 0 16px 40px rgba(92, 53, 160, 0.5)" },
    };

    const virtualDefault = {
        background: "linear-gradient(145deg, #845ec2 0%, #6b46a8 100%)",
        borderColor: "#6b46a8",
        boxShadow: "0 8px 20px rgba(92, 53, 160, 0.25)",
        "&:hover": { boxShadow: "0 10px 28px rgba(92, 53, 160, 0.4)" },
    };

    const virtualDisabledStyle = {
        background: "#e0e0e0",
        borderColor: "#bdbdbd",
        opacity: 0.4,
        cursor: "not-allowed",
    };

    // ── Texto ───────────────────────────────────────────────────────────────
    const titleStyle = {
        color: "white",
        fontWeight: 700,
        fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.2rem" },
        textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
        lineHeight: 1.2,
        marginTop: 1,
    };

    const subtitleStyle = {
        color: "rgba(255,255,255,0.85)",
        fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.8rem" },
        marginTop: 0.5,
        lineHeight: 1.3,
    };

    const iconStyle = {
        fontSize: { xs: 40, sm: 50, md: 60 },
        color: "white",
        filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.25))",
    };

    // ── Estados ─────────────────────────────────────────────────────────────
    const noneSelected = methodology === null || methodology === undefined;
    const presencialSel = methodology === false;
    const virtualSel = methodology === true;

    return (
        <Grid
            container
            spacing={{ xs: 3, sm: 5 }}
            justifyContent="center"
            alignItems="center"
            sx={{ padding: { xs: 2, sm: 3 } }}
        >
            {/* Botón PRESENCIAL (izquierda) */}
            <Grid item>
                <Box
                    onClick={() => onSelectMode(false)}
                    sx={{
                        ...baseCard,
                        ...(presencialSel ? presencialSelected : presencialDefault),
                        opacity: (!noneSelected && !presencialSel) ? 0.72 : 1,
                    }}
                >
                    <BusinessOutlined sx={iconStyle} />
                    <Typography sx={titleStyle}>
                        CURSOS
                        <br />
                        PRESENCIALES
                    </Typography>
                    <Typography sx={subtitleStyle}>
                        Centro de Formación
                        <br />
                        Ciudad de Bs As
                    </Typography>
                </Box>
            </Grid>

            {/* Botón VIRTUAL (derecha) */}
            <Grid item>
                <Box
                    onClick={() => { if (!isVirtualDisabled) onSelectMode(true); }}
                    sx={{
                        ...baseCard,
                        ...(isVirtualDisabled
                            ? virtualDisabledStyle
                            : virtualSel
                                ? virtualSelected
                                : virtualDefault),
                        opacity: (!noneSelected && !virtualSel && !isVirtualDisabled) ? 0.72 : 1,
                    }}
                >
                    <ComputerOutlined sx={iconStyle} />
                    <Typography sx={titleStyle}>
                        CURSOS
                        <br />
                        VIRTUALES
                    </Typography>
                    <Typography sx={subtitleStyle}>
                        Campus Virtual
                        <br />
                        Todo el país
                    </Typography>
                </Box>
            </Grid>
        </Grid>
    );
};

export default ModalitySelector;
